import { useUserStore, WebSocketService } from "@innoclouds/shared";
import dayjs from "dayjs";
import { defineStore } from "pinia";
import { ref } from "vue";
import constants from "../../../constants/constants";
import useAxios from "../../../utils/api/useAxiosToken";
import type { IOrderWithProgress, IWSOrder } from "../../../views/profilePage/iProfile";
import type { IOrderStatusCooking } from "./iOrder";

export const useOrderStore = defineStore("order", () => {
	const userStore = useUserStore();
	const { axiosInstanceAuthenticationGet } = useAxios();

	const ordersProgress = ref<IOrderWithProgress[]>([]);
	const ordersCooking = ref<IOrderStatusCooking[]>([]);
	const ordersReady = ref<any[]>([]);
	const isLoading = ref(false);
	const wsConnections = ref<Map<number, WebSocketService | any>>(new Map<number, WebSocketService>());

	const fetchOrders = async (filters?: { dateFrom?: string; dateTo?: string; isActive?: boolean }) => {
		if (!userStore.isAuthorized || !constants.value.orgId) return;

		try {
			isLoading.value = true;
			ordersCooking.value = [];
			ordersReady.value = [];

			const params = new URLSearchParams({ organization: constants.value.orgId });
			if (filters?.dateFrom) params.set("date_from", filters.dateFrom);
			if (filters?.dateTo) params.set("date_to", filters.dateTo);
			if (filters?.isActive) params.set("is_active", "true");

			const response: any = await axiosInstanceAuthenticationGet.get(`order?${params.toString()}`);
			const rawOrders: any[] = Array.isArray(response.data) ? response.data : (response.data?.results ?? []);

			const HIDDEN_STATUSES = new Set([
				"created",
				"waiting_creation_iiko",
				"failed",
				"sent",
				"failed_after_sent",
				"sent_fiscalization",
				"waiting_for_fiscalization",
				"failed_after_sent_fiscalization",
				"failed_after_payment_initiation",
				"payment_initiation_retry",
				"payment_initiation",
				"cancelled",
				"fiscalized",
				"waiting_for_refund",
				"refunded",
				"refund_failed",
			]);

			const processedOrders = rawOrders
				.filter((item: any) => !HIDDEN_STATUSES.has(item.status) && item.plugin_order)
				.map((item: any) => {
					const statusOrder =
						item.plugin_order.drinksStatus && item.plugin_order.foodStatus
							? Math.min(item.plugin_order.drinksStatus, item.plugin_order.foodStatus)
							: item.plugin_order.drinksStatus || item.plugin_order.foodStatus || 0;

					CheckStatuses(statusOrder, item);

					// Own instance (not getWebSocketInstance): profile orders must not share/close table/payment sockets.
					if (!wsConnections.value.has(item.id) && statusOrder !== 3) {
						const wsService = new WebSocketService(`${import.meta.env.VITE_WS_URL}/ws/order/${item.id}/`, {
							heartbeatTimeout: 30_000,
						});
						wsConnections.value.set(item.id, wsService);

						wsService.on("message", (data: IWSOrder) => {
							if (data.method === "update_cooking_status") {
								const update = data.params;
								const ws_response = update?.item_type_update;

								const statusOrder =
									ws_response === "drink"
										? update?.drinks_status!
										: ws_response === "food"
											? update?.food_status!
											: Math.min(update?.drinks_status!, update?.food_status!) || 0;

								ordersProgress.value = ordersProgress.value.map((order) => {
									if (order.id === item.id) {
										return {
											...order,
											statusOrder,
											progress: calculateProgress(order),
										};
									}
									return order;
								});

								CheckStatuses(statusOrder, item);

								if (statusOrder === 3) {
									wsService.close();
									wsConnections.value.delete(item.id);
								}
							}

							if (data.method === "update_order_status") {
								const nextStatus = data.params?.status;
								if (!nextStatus) return;

								if (HIDDEN_STATUSES.has(nextStatus)) {
									ordersProgress.value = ordersProgress.value.filter((o) => o.id !== item.id);
									wsService.close();
									wsConnections.value.delete(item.id);
									return;
								}

								ordersProgress.value = ordersProgress.value.map((order) => (order.id === item.id ? { ...order, status: nextStatus } : order));
							}
						});

						wsService.connect();
					}

					item.created_at = dayjs(item.created_at).format("DD.MM.YYYY HH:mm");

					return {
						...item,
						created_at: item.created_at,
						statusOrder,
						progress: calculateProgress(item),
					};
				});

			ordersProgress.value = processedOrders;
			return processedOrders;
		} catch (err: any) {
			console.error("[orderStore] fetchOrders failed:", err);
			if (err?.response?.status === 401) return userStore.logout();
			ordersProgress.value = [];
		} finally {
			isLoading.value = false;
		}
	};

	const CheckStatuses = (status: number, item: any) => {
		const timerExpected = item.items.reduce((minTime: number, created_at: any) => {
			return created_at.expected_cooking_time > minTime ? created_at.expected_cooking_time : minTime;
		}, 0);

		if (status === 1) {
			const newCookingOrder = {
				id: item.id,
				created_at: dayjs(item.created_at).format("DD.MM.YYYY HH:mm"),
				number: item.plugin_order.number,
				expected_cooking_time: timerExpected,
			};
			if (!ordersCooking.value.some((order) => order.id === item.id)) {
				ordersCooking.value = [...ordersCooking.value, newCookingOrder].sort((a, b) => parseCreatedAt(a.created_at) - parseCreatedAt(b.created_at));
			}
		} else if (status === 2) {
			if (!ordersReady.value.some((order) => order.order_number === item.plugin_order.number)) {
				ordersReady.value = [
					...ordersReady.value,
					{
						order_number: item.plugin_order.number,
						table_number: item.table.number,
					},
				];
			}
		}
	};

	const parseCreatedAt = (created_at: string) => {
		const [datePart, timePart] = created_at.split(" ");
		const [day, month, year] = datePart.split(".").map(Number);
		const [hours, minutes] = timePart.split(":").map(Number);
		return new Date(year, month - 1, day, hours, minutes).getTime();
	};

	const calculateProgress = (order: any, type = false) => {
		const createdAt = parseCreatedAt(order.created_at);
		const now = Date.now();
		const elapsedTime = (now - createdAt) / 1000;
		const remainingTime = Math.max(order.expected_cooking_time * 60 - elapsedTime, 0);
		const progress = (remainingTime / (order.expected_cooking_time * 60)) * 100;
		if (type) {
			const minutes = Math.floor(remainingTime / 60);
			const seconds = Math.floor(remainingTime % 60);
			return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
		}
		return Math.max(0, Math.min(100, Math.round(progress)));
	};

	const clearWSConnections = () => {
		wsConnections.value.forEach((wsService) => void wsService.close());
		wsConnections.value = new Map();
	};

	return {
		fetchOrders,
		CheckStatuses,
		calculateProgress,
		clearWSConnections,
		parseCreatedAt,
		ordersProgress,
		ordersCooking,
		ordersReady,
		isLoading,
		wsConnections,
	};
});
